import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";
import { Gift, Loader2, ArrowLeft } from "lucide-react";

export default function Redeem() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || localStorage.getItem("pending_gift_code") || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const c = params.get("code");
    if (c) localStorage.setItem("pending_gift_code", c);
  }, [params]);

  const redeem = async () => {
    if (!code.trim()) { toast.error("Please enter your gift code."); return; }
    setBusy(true);
    try {
      await api.post("/promo/redeem", { code: code.trim() });
      localStorage.removeItem("pending_gift_code");
      await refreshUser();
      toast.success("Your gift is unlocked — welcome!");
      navigate("/home", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That code isn't valid. Please check it and try again.");
      setBusy(false);
    }
  };

  const goAuth = (path) => {
    if (code.trim()) localStorage.setItem("pending_gift_code", code.trim());
    navigate(path);
  };

  return (
    <div className="min-h-screen grain bg-cream" data-testid="redeem-page">
      <header className="border-b border-line bg-cream/85">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5"><img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" /><Wordmark size="text-lg sm:text-xl" /></Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-subtle hover:text-charcoal text-sm"><ArrowLeft size={15} /> Home</Link>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 sm:px-6 py-14 relative z-10 text-center">
        <span className="inline-flex w-14 h-14 rounded-2xl bg-coral/10 text-coral items-center justify-center"><Gift size={28} /></span>
        <h1 className="font-serif text-3xl text-charcoal mt-4">Redeem your gift</h1>
        <p className="text-subtle mt-2">Enter the gift code from your email to unlock a full year of Write My Story.</p>

        <div className="mt-8 bg-white rounded-3xl border border-line p-6 sm:p-8 text-left">
          <label className="text-sm font-medium text-charcoal">Your gift code</label>
          <input data-testid="redeem-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. GIFT-1A2B3C4D" onKeyDown={(e) => e.key === "Enter" && user && redeem()}
            className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line outline-none focus:border-teal tracking-widest font-serif" />

          {user ? (
            <button data-testid="redeem-submit-btn" onClick={redeem} disabled={busy}
              className="mt-4 w-full h-13 py-3.5 rounded-full bg-coral hover:bg-coraldark text-white font-semibold text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" /> : <>Unlock my gift</>}
            </button>
          ) : (
            <div className="mt-5">
              <p className="text-sm text-subtle">First, create your free account (or log in) — then your code will be waiting right here to claim.</p>
              <div className="mt-4 flex flex-col gap-3">
                <button data-testid="redeem-register-btn" onClick={() => goAuth("/register")}
                  className="w-full h-12 rounded-full bg-coral hover:bg-coraldark text-white font-semibold transition-colors">Create my free account</button>
                <button data-testid="redeem-login-btn" onClick={() => goAuth("/login")}
                  className="w-full h-12 rounded-full bg-white border border-line text-charcoal font-semibold hover:border-sage transition-colors">I already have an account</button>
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-subtle mt-6">Trouble with your code? Email <a href="mailto:julietsullivan@outlook.com" className="text-teal underline">julietsullivan@outlook.com</a></p>
      </main>
    </div>
  );
}
